import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useAccessWindowSize } from "../../layout/useAccessWindowSize";
import { POPOVER_RESTRICTED } from "./constants";
import useModal from "./useModal";
import { autoUpdate, flip, offset, useFloating } from "@floating-ui/react-dom";
import { useInteractions } from "@floating-ui/react";

interface PopoverProps {
	buttonContent?: React.ReactNode;
	children: React.ReactNode;
	classes?: string;
	modalId: string;
	toggleClasses?: string;
	offsetValue?:
		| number
		| {
				mainAxis?: number;
				crossAxis?: number;
				alignmentAxis?: number | null;
		  };
}

export function Popover({
	buttonContent,
	children,
	classes,
	modalId,
	toggleClasses,
	offsetValue = 0,
}: PopoverProps) {
	const { openedModals, onCloseModal, onOpenModal } = useModal();
	const { isMobile } = useAccessWindowSize();

	const [referenceElement, setReferenceElement] = useState(null);
	const [floatingElement, setFloatingElement] = useState(null);

	const { floatingStyles, placement } = useFloating({
		placement: "bottom-start",
		elements: { reference: referenceElement, floating: floatingElement },
		middleware: [offset(offsetValue), flip()],
		whileElementsMounted: autoUpdate,
	});

	const { getReferenceProps, getFloatingProps } = useInteractions();

	const showTooltip = () => {
		// @ts-ignore
		floatingElement?.setAttribute("data-show", "");
	};

	const hideTooltip = () => {
		// @ts-ignore
		floatingElement?.removeAttribute("data-show");
	};

	useEffect(() => {
		if (!isMobile) {
			document.documentElement.classList.remove(
				"backdrop--fixed--opened",
			);

			[
				["mouseenter", showTooltip],
				["mouseleave", hideTooltip],
				["focus", showTooltip],
				["blur", hideTooltip],
			].forEach(([event, listener]) => {
				return (
					referenceElement &&
					// @ts-ignore
					referenceElement.parentElement.addEventListener(
						event,
						listener,
					)
				);
			});

			return () => {
				[
					["mouseenter", showTooltip],
					["mouseleave", hideTooltip],
					["focus", showTooltip],
					["blur", hideTooltip],
				].forEach(([event, listener]) => {
					referenceElement &&
						// @ts-ignore
						referenceElement.parentElement.removeEventListener(
							event,
							listener,
						);
				});
			};
		}
	}, [isMobile, referenceElement]);

	const handleOpenPopover = () => {
		if (isMobile) {
			showTooltip();
			onOpenModal(POPOVER_RESTRICTED);
			document.documentElement.classList.add("backdrop--fixed--opened");
		}
	};

	const handleClosePopover = () => {
		if (isMobile) {
			hideTooltip();
			onCloseModal();
			document.documentElement.classList.remove(
				"backdrop--fixed--opened",
			);
		}
	};
	return (
		<div
			className={clsx("popover", classes)}
			aria-expanded={isMobile && openedModals.includes(modalId)}
		>
			<button
				type="button"
				// @ts-ignore
				ref={setReferenceElement}
				className={clsx("btn", toggleClasses)}
				onClick={handleOpenPopover}
				{...getReferenceProps}
			>
				{buttonContent}
			</button>

			<div
				className="popover-content"
				// @ts-ignore
				ref={setFloatingElement}
				style={floatingStyles}
				data-placement={placement}
				{...getFloatingProps()}
			>
				<div className="content">
					<button
						type="button"
						className="btn btn--close"
						onClick={handleClosePopover}
					>
						Close
					</button>
					{children}
					<button
						type="button"
						className="btn btn--secondary btn--cancel"
						onClick={handleClosePopover}
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	);
}
